import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Alert,
    CircularProgress,
    Avatar,
    Chip,
    Divider
} from '@mui/material';
import { CalendarToday, AccessTime } from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useDailyTrackerStore from '@store/DailyTrackerStore';

// Type definitions using backend variable names
interface DailyUpdate {
    update_id: number;
    employee_id: number;
    employee_name: string;
    employee_email: string;
    employee_role: string;
    attendance_date: string;
    punch_in: string;
    punch_out: string | null;
    total_hours: string | null;
    daily_status: string;
    daily_update: string;
}

const DailyUpdatesFeedPage: React.FC = () => {
    const {
        dailyUpdatesByDate,
        loading,
        error,
        fetchDailyUpdatesByDate,
        resetErrors
    } = useDailyTrackerStore();

    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const currentDate = new Date().toISOString().split('T')[0];

    // Fetch today's updates on initial load
    useEffect(() => {
        fetchDailyUpdatesByDate(currentDate);
    }, [fetchDailyUpdatesByDate, currentDate]);

    // Fetch updates by date when filter changes
    useEffect(() => {
        if (filterDate !== currentDate) {
            fetchDailyUpdatesByDate(filterDate);
        }
    }, [filterDate, fetchDailyUpdatesByDate, currentDate]);

    const handleDateChange = (newDate: string) => {
        setFilterDate(newDate);
        fetchDailyUpdatesByDate(newDate);
    };

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        }
    };

    const formatTime = (timeString: string) => {
        return new Date(timeString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const updates = dailyUpdatesByDate;

    if (loading && !updates) {
        return (
            <Layout>
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress size={60} />
                    </Box>
                </Container>
            </Layout>
        );
    }

    return (
        <Layout>
            <Container maxWidth="xl">
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Header */}
                    <Box sx={{
                        width: '100%',
                        maxWidth: '800px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 4,
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom fontWeight="600">
                                Daily Updates Feed
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary">
                                View daily updates from all employees
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <TextField
                                type="date"
                                label="Select Date"
                                value={filterDate}
                                onChange={(e) => handleDateChange(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    max: currentDate
                                }}
                                size="small"
                                sx={{
                                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                        filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                                    }
                                }}
                            />
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3, width: '100%', maxWidth: '800px' }} onClose={resetErrors}>
                            {error}
                        </Alert>
                    )}

                    {/* Updates Feed */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        width: '100%',
                        maxWidth: '800px'
                    }}>
                        {updates && updates.length > 0 ? (
                            updates.map((update: DailyUpdate) => (
                                <Card
                                    key={update.update_id}
                                    elevation={2}
                                    sx={{
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&:hover': {
                                            boxShadow: 6,
                                            transform: 'translateY(-4px)',
                                            borderColor: 'primary.main'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        {/* Header with Employee Info */}
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: 'primary.main',
                                                    width: 56,
                                                    height: 56,
                                                    mr: 2,
                                                    fontSize: '1.25rem',
                                                    fontWeight: 600,
                                                    boxShadow: 2
                                                }}
                                            >
                                                {getInitials(update.employee_name)}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                                                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.1rem' }}>
                                                        {update.employee_name}
                                                    </Typography>
                                                    <Chip
                                                        label={update.employee_role}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                            bgcolor: 'primary.light',
                                                            color: 'primary.contrastText'
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                        <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                                                            {formatDate(update.attendance_date)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                        <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                                                            {formatTime(update.punch_in)}
                                                            {update.punch_out && ` - ${formatTime(update.punch_out)}`}
                                                        </Typography>
                                                    </Box>
                                                    {update.total_hours && (
                                                        <Chip
                                                            label={`${update.total_hours} hrs`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'success.light',
                                                                color: 'success.contrastText',
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2.5 }} />

                                        {/* Daily Update Content */}
                                        <Box sx={{
                                            bgcolor: 'background.default',
                                            borderRadius: 2,
                                            p: 2.5,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            <Box
                                                sx={{
                                                    lineHeight: 1.7,
                                                    color: 'text.primary',
                                                    fontSize: '0.95rem',
                                                    '& p': {
                                                        margin: '0.5em 0',
                                                        '&:first-of-type': { marginTop: 0 },
                                                        '&:last-child': { marginBottom: 0 }
                                                    },
                                                    '& ul, & ol': {
                                                        paddingLeft: '1.5rem',
                                                        margin: '0.5em 0',
                                                        '& li': {
                                                            marginBottom: '0.25em'
                                                        }
                                                    },
                                                    '& strong': {
                                                        fontWeight: 700
                                                    },
                                                    '& em': {
                                                        fontStyle: 'italic'
                                                    }
                                                }}
                                                dangerouslySetInnerHTML={{ __html: update.daily_update }}
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))
                        ) : (
                            <Card
                                elevation={2}
                                sx={{
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography variant="h6" fontWeight="600">
                                            No daily updates found
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            No updates available for {formatDate(filterDate)}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                    </Box>

                    {/* Loading indicator for subsequent loads */}
                    {loading && updates && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <CircularProgress size={40} />
                        </Box>
                    )}
                </Box>
            </Container>
        </Layout>
    );
};

export default DailyUpdatesFeedPage;